import { INTEREST_CATEGORY, getInterestCategory } from "@component/Mortgage/Calculator/domain";
import type { InterestCategory, MortgageResponseItem } from "@component/Mortgage/Calculator/types";
import type { AbstractStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { Button } from "bm-ui-kit";
import type { FormApi } from "final-form";
import groupBy from "lodash/groupBy";
import React from "react";
import type { ReactNode } from "react";
import { Card, Col, Row } from "react-bootstrap";

export interface StepFields {}

interface Props extends AbstractStepProps {
	loanAmount: number;
	maturity: number;
	results: MortgageResponseItem[];

	onSelect: (index: number, result: MortgageResponseItem) => void;
}

export class PartnerResult extends AbstractStep<StepFields, Props> {
	protected renderContent(_values: StepFields, _form: FormApi<StepFields>): ReactNode {
		const { loanAmount, maturity, results } = this.props;

		return (
			<>
				<Card.Header className="bg-success">
					<Card.Title className="text-white text-center">
						<span className="bg-white text-success px-1">
							{formatText(Math.floor(loanAmount / 1_000_000), "m ft")}
						</span>{" "}
						hitel <span className="bg-white text-success px-1">{maturity} év</span> futamidőre:
					</Card.Title>
				</Card.Header>
				<Card.Body className="py-2">
					<div className="text-center mb-2 font-weight-bold">Kategóriánként a legolcsóbbak:</div>
					{this.renderResults()}
				</Card.Body>
				<Card.Footer className="text-center">
					<Button onClick={() => this.onSelect(0, results[0])} size="lg">
						Kérem a szakértő segítségét
					</Button>
				</Card.Footer>
			</>
		);
	}

	private renderResults(): ReactNode {
		const { results, maturity } = this.props;
		const groupedResults = groupBy(results, (item) => getInterestCategory(maturity, item));

		return (
			<>
				{INTEREST_CATEGORY.map((interestPeriod, index) =>
					this.renderResult(groupedResults[interestPeriod], interestPeriod, index),
				)}
			</>
		);
	}

	private renderResult(results: MortgageResponseItem[], type: InterestCategory, index: number): ReactNode {
		if (!results?.length) {
			return null;
		}
		const { id, apr, installmentStart } = results[0];

		return (
			<Card
				key={id}
				className="mb-2 cursor-pointer border partner_result_item"
				onClick={() => this.onSelect(index, results[0])}
			>
				<Card.Header className={`text-left px-2 py-1 period border-${this.getInterestColor(type)} rounded-0`}>
					<Card.Title className="font-weight-bold" as="div">
						{this.getInterestPeriodName(type)}
					</Card.Title>
				</Card.Header>
				<Card.Body className="p-1">
					<Row className="text-center">
						<Col xs={7}>
							<strong>{formatText(installmentStart, "ft")}</strong>
							<br />
							Havi törlesztő
						</Col>
						<Col xs={5} className="border-left">
							<strong>{formatText(apr, "%")}</strong>
							<br />
							THM
						</Col>
					</Row>
				</Card.Body>
			</Card>
		);
	}

	@autobind
	private onSelect(index: number, result: MortgageResponseItem): void {
		this.props.onSelect(index, result);

		this.onStep("lead-form");
	}

	private getInterestPeriodName(type: InterestCategory): string {
		switch (type) {
			case "fix":
				return "Végig fix kamatozás";
			case "ten":
				return "10 évig fix kamatozás";
			case "five":
				return "5 évig fix kamatozás";
			case "short":
			default:
				return "Változó kamatozás";
		}
	}

	private getInterestColor(type: InterestCategory): string {
		switch (type) {
			case "short":
				return "danger";
			case "five":
				return "warning";
			case "ten":
				return "gold";
			case "fix":
			default:
				return "success";
		}
	}

	protected getComponentName(): string {
		return "PartnerResult";
	}
}
